import { HttpClient } from './http-client';
import { 
  OrderSummaryDto,
  OrderDetailDto,
  PendingApprovalDto,
  DivisionStatsDto,
  PagedResult,
  DashboardSummary,
  ApiError
} from '../types';

export class OrderService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Get all orders for a division
   */
  async getOrders(
    divisionId: string,
    params?: {
      page?: number;
      limit?: number;
      status?: string;
      submitterId?: string;
      startDate?: string;
      endDate?: string;
      updatedStartDate?: string;
      updatedEndDate?: string;
      minAmount?: number;
      maxAmount?: number;
      search?: string;
      step?: number;
      pendingOnly?: boolean;
      workflowId?: string;
      workflowName?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PagedResult<OrderSummaryDto>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.submitterId) searchParams.append('submitterId', params.submitterId);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.updatedStartDate) searchParams.append('updatedStartDate', params.updatedStartDate);
    if (params?.updatedEndDate) searchParams.append('updatedEndDate', params.updatedEndDate);
    if (params?.minAmount) searchParams.append('minAmount', params.minAmount.toString());
    if (params?.maxAmount) searchParams.append('maxAmount', params.maxAmount.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.step) searchParams.append('step', params.step.toString());
    if (params?.pendingOnly) searchParams.append('pendingOnly', params.pendingOnly.toString());
    if (params?.workflowId) searchParams.append('workflowId', params.workflowId);
    if (params?.workflowName) searchParams.append('workflowName', params.workflowName);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions/${divisionId}/orders${queryString ? '?' + queryString : ''}`;
    
    try {
      const response = await this.httpClient.get<PagedResult<OrderSummaryDto>>(url);
      return response;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }

  /**
   * Get pending approvals for a division with filtering and sorting
   */
  async getPendingApprovals(
    divisionId: string,
    params?: {
      userId?: string;
      status?: string;
      startDate?: string;
      endDate?: string;
      search?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      page?: number;
      limit?: number;
    }
  ): Promise<PagedResult<PendingApprovalDto>> {
    const searchParams = new URLSearchParams();
    
    if (params?.userId) searchParams.append('userId', params.userId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions/${divisionId}/orders/pending-approvals${queryString ? '?' + queryString : ''}`;
    
    try {
      const response = await this.httpClient.get<PagedResult<PendingApprovalDto>>(url);
      return response;
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      throw error;
    }
  }

  /**
   * Get orders requiring approval from the current user
   */
  async getOrdersRequiringApproval(divisionId: string): Promise<OrderSummaryDto[]> {
    const url = `/api/v1.0/divisions/${divisionId}/orders/requiring-approval`;
    
    try {
      const response = await this.httpClient.get<OrderSummaryDto[]>(url);
      return response;
    } catch (error) {
      console.error('Error fetching orders requiring approval:', error);
      throw error;
    }
  }

  /**
   * Get all orders in approval process that user is involved with (pending or approved)
   */
  async getOrdersInApproval(
    divisionId: string, 
    filters?: {
      status?: string;
      startDate?: string;
      endDate?: string;
      search?: string;
      submitterId?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      page?: number;
      limit?: number;
      workflowId?: string;
    }
  ): Promise<PagedResult<OrderSummaryDto>> {
    const url = `/api/v1.0/divisions/${divisionId}/orders/in-approval`;
    
    // Build query parameters
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.submitterId) params.append('submitterId', filters.submitterId);
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.workflowId) params.append('workflowId', filters.workflowId);

    try {
      const queryString = params.toString();
      const fullUrl = queryString ? `${url}?${queryString}` : url;
      const response = await this.httpClient.get<PagedResult<OrderSummaryDto>>(fullUrl);
      return response;
    } catch (error) {
      console.error('Error fetching orders in approval:', error);
      throw error;
    }
  }

  /**
   * Get division statistics for dashboard
   */
  async getDivisionStats(
    divisionId: string,
    params?: {
      startDate?: string;
      endDate?: string;
    }
  ): Promise<DivisionStatsDto> {
    const searchParams = new URLSearchParams();
    
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions/${divisionId}/orders/stats${queryString ? '?' + queryString : ''}`;
    
    try {
      const response = await this.httpClient.get<DivisionStatsDto>(url);
      return response;
    } catch (error) {
      console.error('Error fetching division stats:', error);
      throw error;
    }
  }

  /**
   * Get order detail by ID
   */
  async getOrderDetail(divisionId: string, orderId: string): Promise<OrderDetailDto> {
    const url = `/api/v1/divisions/${divisionId}/orders/${orderId}`;
    
    try {
      const response = await this.httpClient.get<OrderDetailDto>(url);
      return response;
    } catch (error) {
      console.error('Error fetching order detail:', error);
      throw error;
    }
  }

  /**
   * Get dashboard summary data
   */
  async getDashboardSummary(divisionId: string): Promise<{
    totalOrders: number;
    actionRequiredOrders: number;
    ordersAssignedToUser: number;
    completedOrders: number;
  }> {
    try {
      // Fetch multiple endpoints concurrently
      const [allOrders, actionRequired, assignedToUser] = await Promise.all([
        this.getOrders(divisionId, { limit: 1 }), // Just get count
        this.getPendingApprovals(divisionId, { limit: 1 }), // Just get count
        this.getOrdersRequiringApproval(divisionId)
      ]);

      // Get completed orders count
      const completedOrders = await this.getOrders(divisionId, { 
        status: 'Completed',
        limit: 1 
      });

      return {
        totalOrders: allOrders.totalCount,
        actionRequiredOrders: actionRequired.totalCount,
        ordersAssignedToUser: assignedToUser.length,
        completedOrders: completedOrders.totalCount
      };
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  }

  /**
   * Get submitted orders for a division
   */
  async getSubmittedOrders(
    divisionId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      submitterId?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PagedResult<OrderSummaryDto>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.submitterId) searchParams.append('submitterId', params.submitterId);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions/${divisionId}/orders/submitted${queryString ? '?' + queryString : ''}`;
    
    try {
      const response = await this.httpClient.get<PagedResult<OrderSummaryDto>>(url);
      return response;
    } catch (error) {
      console.error('Error fetching submitted orders:', error);
      throw error;
    }
  }

  /**
   * Get paused orders for a division
   */
  async getPausedOrders(
    divisionId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      submitterId?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PagedResult<OrderSummaryDto>> {
    const searchParams = new URLSearchParams();
    searchParams.append('status', 'Paused');
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.submitterId) searchParams.append('submitterId', params.submitterId);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions/${divisionId}/orders${queryString ? '?' + queryString : ''}`;
    
    try {
      const response = await this.httpClient.get<PagedResult<OrderSummaryDto>>(url);
      return response;
    } catch (error) {
      console.error('Error fetching paused orders:', error);
      throw error;
    }
  }

  async getCompletedOrders(
    divisionId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      submitterId?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PagedResult<OrderSummaryDto>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.submitterId) searchParams.append('submitterId', params.submitterId);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions/${divisionId}/orders/completed${queryString ? '?' + queryString : ''}`;
    
    try {
      const response = await this.httpClient.get<PagedResult<OrderSummaryDto>>(url);
      return response;
    } catch (error) {
      console.error('Error fetching completed orders:', error);
      throw error;
    }
  }

  async getCancelledOrders(
    divisionId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      submitterId?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PagedResult<OrderSummaryDto>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.submitterId) searchParams.append('submitterId', params.submitterId);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions/${divisionId}/orders/cancelled${queryString ? '?' + queryString : ''}`;
    
    try {
      const response = await this.httpClient.get<PagedResult<OrderSummaryDto>>(url);
      return response;
    } catch (error) {
      console.error('Error fetching cancelled orders:', error);
      throw error;
    }
  }

  async getNeedsResolvedOrders(
    divisionId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PagedResult<OrderSummaryDto>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions/${divisionId}/orders/needs-resolved${queryString ? '?' + queryString : ''}`;
    
    try {
      const response = await this.httpClient.get<PagedResult<OrderSummaryDto>>(url);
      return response;
    } catch (error) {
      console.error('Error fetching needs resolved orders:', error);
      throw error;
    }
  }

  /**
   * Create a new external order
   */
  async createExternalOrder(orderData: any): Promise<any> {
    const url = '/api/external/orders/json';
    
    try {
      const response = await this.httpClient.post(url, orderData);
      return response;
    } catch (error) {
      console.error('Error creating external order:', error);
      throw error;
    }
  }

  /**
   * Trigger reprocess of approvers for an order in NeedsResolved status
   */
  async reprocessApprovers(divisionId: string, orderId: string): Promise<{ message: string }> {
    const url = `/api/v1/divisions/${divisionId}/orders/${orderId}/reprocess-approvers`;
    
    try {
      const response = await this.httpClient.post<{ message: string }>(url, {});
      return response;
    } catch (error) {
      console.error('Error reprocessing approvers:', error);
      throw error;
    }
  }

  /**
   * Restart the workflow for an order in NeedsResolved status (when workflow doesn't exist)
   */
  async restartWorkflow(divisionId: string, orderId: string): Promise<{ message: string; workflowId: string }> {
    const url = `/api/v1/divisions/${divisionId}/orders/${orderId}/restart-workflow`;
    
    try {
      const response = await this.httpClient.post<{ message: string; workflowId: string }>(url, {});
      return response;
    } catch (error) {
      console.error('Error restarting workflow:', error);
      throw error;
    }
  }

  /**
   * Reprocess workflow matching for an order in Submitted status without a workflow
   */
  async reprocessOrder(divisionId: string, orderId: string): Promise<{ 
    message: string; 
    orderId: string;
    workflowId?: string;
    workflowName?: string;
    status: string;
  }> {
    const url = `/api/v1/divisions/${divisionId}/orders/${orderId}/reprocess`;
    
    try {
      const response = await this.httpClient.post<{ 
        message: string; 
        orderId: string;
        workflowId?: string;
        workflowName?: string;
        status: string;
      }>(url, {});
      return response;
    } catch (error) {
      console.error('Error reprocessing order:', error);
      throw error;
    }
  }

  /**
   * Pause a running workflow for an order
   */
  async pauseOrder(divisionId: string, orderId: string, reason?: string): Promise<{ message: string; workflowId: string; status: string; reason?: string }> {
    const url = `/api/v1/divisions/${divisionId}/orders/${orderId}/pause`;
    
    try {
      const response = await this.httpClient.post<{ message: string; workflowId: string; status: string; reason?: string }>(
        url, 
        { reason }
      );
      return response;
    } catch (error) {
      console.error('Error pausing order:', error);
      throw error;
    }
  }

  /**
   * Resume a paused workflow for an order
   */
  async resumeOrder(divisionId: string, orderId: string): Promise<{ message: string; workflowId: string; status: string }> {
    const url = `/api/v1/divisions/${divisionId}/orders/${orderId}/resume`;
    
    try {
      const response = await this.httpClient.post<{ message: string; workflowId: string; status: string }>(url, {});
      return response;
    } catch (error) {
      console.error('Error resuming order:', error);
      throw error;
    }
  }

  /**
   * Permanently delete an order and close any open Temporal workflow (admin only).
   */
  async deleteOrder(divisionId: string, orderId: string): Promise<void> {
    const url = `/api/v1/divisions/${divisionId}/orders/${orderId}`;
    try {
      await this.httpClient.delete<void>(url);
    } catch (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  }

  /**
   * Get the live Temporal workflow status for an order (admin only)
   */
  async getTemporalStatus(divisionId: string, orderId: string): Promise<{
    temporalWorkflowId: string;
    dbStatus: string;
    temporalStatus: string;
    isNotFound: boolean;
    dbStatusUpdated: boolean;
    isInSync: boolean;
  }> {
    const url = `/api/v1/divisions/${divisionId}/orders/${orderId}/temporal-status`;
    try {
      const response = await this.httpClient.get<{
        temporalWorkflowId: string;
        dbStatus: string;
        temporalStatus: string;
        isNotFound: boolean;
        dbStatusUpdated: boolean;
        isInSync: boolean;
      }>(url);
      return response;
    } catch (error) {
      console.error('Error fetching Temporal status:', error);
      throw error;
    }
  }
}